import { Dispatch, SetStateAction } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { ContentModal } from 'components/modal';
import { GerarReembolsoQuinzenaAutoComplete } from 'pages/financeiro/reembolso/gerar-reembolso/components';
import { useMutateReembolso } from 'pages/financeiro/reembolso/gerar-reembolso/hooks';
import { FormikApuracao } from 'pages/financeiro/reembolso/gerar-reembolso/types';

type GerarReembolsoApuracaoModal = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
};

export function GerarReembolsoApuracaoModal({ open, setOpen }: GerarReembolsoApuracaoModal) {
    const { handleSubmit, isLoading } = useMutateReembolso({ setOpen });

    const validation = Yup.object({
        ano: Yup.date().required(),
        mes: Yup.date().required(),
        quinzena: Yup.object().required(),
    });

    return (
        <ContentModal
            open={open}
            setOpen={setOpen}
            disableEscapeKeyDown={isLoading}
            disableBackdropClick={isLoading}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderRadius: 1,
                minWidth: { xs: '90dvw', sm: 600 },
            }}
        >
            <Formik<FormikApuracao>
                validationSchema={validation}
                onSubmit={handleSubmit}
                validateOnChange={false}
                initialValues={{
                    ano: null,
                    mes: null,
                    quinzena: null,
                }}
            >
                {({ values, setFieldValue, errors, resetForm, submitForm }) => (
                    <>
                        <Typography variant="h6">Código da Apuração</Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Ano"
                                    format="yyyy"
                                    value={values.ano}
                                    views={['year']}
                                    maxDate={new Date()}
                                    onChange={(value) => setFieldValue('ano', value)}
                                    slotProps={{
                                        textField: {
                                            autoComplete: 'off',
                                            size: 'small',
                                            disabled: isLoading,
                                            error: !!errors.ano,
                                            helperText: typeof errors.ano === 'string' && errors.ano,
                                            sx: { flex: 1 },
                                        },
                                    }}
                                />

                                <DatePicker
                                    label="Mês"
                                    format="MMMM"
                                    views={['month']}
                                    value={values.mes}
                                    onChange={(value) => setFieldValue('mes', value)}
                                    slotProps={{
                                        textField: {
                                            autoComplete: 'off',
                                            size: 'small',
                                            disabled: isLoading,
                                            error: !!errors.mes,
                                            helperText: typeof errors.mes === 'string' && errors.mes,
                                            sx: { flex: 1 },
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                            <GerarReembolsoQuinzenaAutoComplete
                                sx={{ flex: 1 }}
                                disabled={isLoading}
                                error={errors.quinzena}
                                value={values.quinzena}
                                onChange={(e, value) => setFieldValue('quinzena', value)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                            <Button
                                variant="outlined"
                                disabled={isLoading}
                                onClick={() => {
                                    setOpen(false);

                                    resetForm();
                                }}
                            >
                                Cancelar
                            </Button>

                            <Button loading={isLoading} variant="contained" onClick={submitForm}>
                                Processar
                            </Button>
                        </Box>
                    </>
                )}
            </Formik>
        </ContentModal>
    );
}
